import { ComponentPropsWithoutRef, PropsWithChildren, ReactElement, ReactNode } from 'react'

import { CheckSuccessDuoTone } from '@ps/ds/icons/CheckSuccessDuoTone'
import { ErrorDuoTone } from '@ps/ds/icons/ErrorDuoTone'
import { HourglassFilled } from '@ps/ds/icons/HourglassFilled'
import { InfoDuoTone } from '@ps/ds/icons/InfoDuoTone'
import { TipFilled } from '@ps/ds/icons/TipFilled'
import { WarningDuoTone } from '@ps/ds/icons/WarningDuoTone'
import { cn } from '@ps/ds/lib/utils'
import { Alert as AlertContainer, AlertDescription, AlertTitle } from '@ps/ds/ui/alert'
import { VariantProps, cva } from 'class-variance-authority'

const alertGradientIcons = {
  info: InfoDuoTone,
  success: CheckSuccessDuoTone,
  warning: WarningDuoTone,
  error: ErrorDuoTone,
  tip: TipFilled,
  pending: HourglassFilled,
} as const

const alertVariants = cva('min-w-0 max-w-none', {
  variants: {
    variant: {
      info: 'bg-navy-100',
      success: 'bg-emerald-100',
      warning: 'bg-yellow-100',
      error: 'bg-destructive-background',
      tip: 'bg-orange-100',
      pending: 'bg-transparent',
    },
    shadow: {
      true: 'shadow-xs',
      false: '',
    },
  },
  defaultVariants: {
    shadow: false,
  },
})

export type AlertProps = Omit<ComponentPropsWithoutRef<typeof AlertContainer>, 'variant'> &
  VariantProps<typeof alertVariants> & {
    title?: string
    description: ReactNode
    icon?: ReactElement
    shadow?: boolean
    className?: string
  }

export const Alert = ({
  title,
  description,
  variant = 'info',
  shadow = false,
  icon: customIcon,
  className,
  children,
  ...props
}: PropsWithChildren<AlertProps>) => {
  const AlertIcon = (variant && alertGradientIcons[variant]) ?? alertGradientIcons.info

  const withChildrenStyle =
    'flex flex-col gap-2 w-full items-start justify-between sm:flex-row sm:gap-4 sm:items-center'

  return (
    <AlertContainer
      {...props}
      className={cn(
        'flex gap-3',
        title ? 'items-start' : 'items-center',
        alertVariants({ variant, shadow }),
        className,
      )}>
      <span className="shrink-0">{customIcon ? customIcon : <AlertIcon className="size-6" />}</span>

      <div className={children ? withChildrenStyle : ''}>
        <div className="flex flex-1 flex-col">
          {title ? (
            <AlertTitle className="font-brand font-semibold mt-1 mb-2">{title}</AlertTitle>
          ) : null}
          <AlertDescription>{description}</AlertDescription>
        </div>

        {children}
      </div>
    </AlertContainer>
  )
}
